/**
 * PaymentEditModal — privileged edit of a single deal_payments row.
 *
 * Added May 20, 2026 (per Greg). Lets super-users + finance/exec tier
 * (admin / finance / sales_director / director_of_operations / roof_director)
 * correct any field on an existing payment: amount, method, slot, kind, state,
 * iink flag, effective date, notes. Deletion is handled separately in
 * CheckTrackerCard.
 *
 * The post_build_balance_remaining / *_amount_received summary columns are
 * recomputed by the deal_payments triggers on UPDATE, so changing amount/slot/
 * state here keeps the deal balance honest with no extra wiring.
 */

import { useState } from "react";
import { supabase } from "../../lib/supabase";
import type { DealPayment, PaymentState, PaymentMethod, PaymentSlot, PaymentKind } from "../../lib/types";
import { formatSupabaseError } from "../../lib/formatSupabaseError";

const METHODS: PaymentMethod[] = ["check", "stripe", "ach", "wire", "cash"];
const SLOTS: PaymentSlot[] = ["pre_build", "post_build", "supplement", "other"];
const KINDS: Array<{ value: string; label: string }> = [
  { value: "", label: "—" },
  { value: "insurance", label: "Insurance" },
  { value: "personal", label: "HO personal" },
];
const STATES: PaymentState[] = [
  "collected", "iink_submitted", "mortgage_endorsed", "fedex_out", "fedex_back",
  "iink_cleared", "deposited", "bank_cleared",
  "pending", "made", "cleared", "disputed", "refunded",
  "voided",
];

// Which timestamp column an "effective date" maps to for a given state.
const STATE_TS_COL: Partial<Record<PaymentState, string>> = {
  collected: "collected_at",
  iink_submitted: "iink_submitted_at",
  mortgage_endorsed: "mortgage_endorsed_at",
  fedex_out: "fedex_out_at",
  fedex_back: "fedex_back_at",
  iink_cleared: "iink_cleared_at",
  deposited: "deposited_at",
  bank_cleared: "bank_cleared_at",
  made: "stripe_made_at",
  cleared: "stripe_cleared_at",
  disputed: "stripe_disputed_at",
  refunded: "stripe_refunded_at",
};

function tsToDateInput(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export default function PaymentEditModal({
  payment,
  onClose,
  onSaved,
}: {
  payment: DealPayment;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState<string>(String(payment.amount ?? ""));
  const [method, setMethod] = useState<PaymentMethod>(payment.method);
  const [slot, setSlot] = useState<PaymentSlot>(payment.slot);
  const [kind, setKind] = useState<string>(payment.kind ?? "");
  const [state, setState] = useState<PaymentState>(payment.state);
  const [iinkRequired, setIinkRequired] = useState<boolean>(payment.iink_required);
  const [notes, setNotes] = useState<string>(payment.notes ?? "");
  const currentTsCol = STATE_TS_COL[payment.state];
  const [effDate, setEffDate] = useState<string>(
    tsToDateInput(currentTsCol ? (payment[currentTsCol as keyof DealPayment] as string | null) : null),
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountNum = Number(amount);
  const amountValid = amount.trim() !== "" && !isNaN(amountNum) && amountNum >= 0;
  const tsColForState = STATE_TS_COL[state];

  async function handleSave() {
    if (!amountValid) {
      setError("Enter a valid amount.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const update: Record<string, unknown> = {
        amount: amountNum,
        method,
        slot,
        kind: kind === "" ? null : (kind as PaymentKind),
        state,
        iink_required: iinkRequired,
        notes: notes.trim() === "" ? null : notes.trim(),
      };
      // Map the effective date onto the timestamp column for the (possibly new)
      // state so the lifecycle visualization + reporting stay coherent. Use noon
      // to dodge the date-only timezone off-by-one trap.
      if (tsColForState) {
        update[tsColForState] = effDate ? new Date(effDate + "T12:00:00").toISOString() : null;
      }
      const { error: err } = await supabase.from("deal_payments").update(update).eq("id", payment.id);
      if (err) throw err;
      onSaved();
    } catch (e) {
      setError(formatSupabaseError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="w-full sm:max-w-md bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-500">Edit payment</div>
            <div className="text-base font-semibold text-bardin-ink">Payment details</div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 -mr-2 text-slate-500 hover:text-bardin-ink rounded-lg hover:bg-slate-100 disabled:opacity-40"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <Field label="Amount ($)">
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Method">
              <Select value={method} onChange={(v) => setMethod(v as PaymentMethod)} options={METHODS.map((m) => ({ value: m, label: m }))} />
            </Field>
            <Field label="Slot">
              <Select value={slot} onChange={(v) => setSlot(v as PaymentSlot)} options={SLOTS.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Kind">
              <Select value={kind} onChange={setKind} options={KINDS} />
            </Field>
            <Field label="State">
              <Select value={state} onChange={(v) => setState(v as PaymentState)} options={STATES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))} />
            </Field>
          </div>

          <Field label={tsColForState ? "Effective date" : "Effective date (n/a for this state)"}>
            <input
              type="date"
              value={effDate}
              disabled={!tsColForState}
              onChange={(e) => setEffDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/40 disabled:bg-slate-100 disabled:text-slate-400"
            />
          </Field>

          {(method === "check" || method === "ach" || method === "wire") && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={iinkRequired} onChange={(e) => setIinkRequired(e.target.checked)} className="rounded" />
              iink endorsement required
            </label>
          )}

          <Field label="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
              placeholder="Reference #, correction reason, etc."
            />
          </Field>

          {error && <div className="text-xs text-red-600 bg-red-50 rounded px-2 py-1.5">⚠ {error}</div>}
        </div>

        <div className="flex gap-2 justify-end px-5 py-3 border-t border-slate-200">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !amountValid}
            className="px-4 py-1.5 text-sm font-semibold rounded-lg bg-bardin-orange text-white hover:opacity-90 disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-bardin-orange/40 capitalize"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
